/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import * as actionTypes from '../constants/action-types';
import { apiClient } from './api'
import { handleError } from './error';
import queryString from 'query-string';
import type { AppDispatch, RootState } from '../reducers';
import type { UtrValueType } from '../types/universalTracker';
import type { UtrvHistory, UtrvHistoryInfoInterface } from '../types/universalTrackerValue';
import type { UTRSuggestion } from '../types/search';
import type { ApiResponse } from '../types/api';

type GetState = () => RootState;

type ValueInfoDetailsResponse =
  | UtrvHistoryInfoInterface[]
  | { data: UtrvHistoryInfoInterface[]; providers?: UtrvHistoryInfoInterface['providers'] };

export function loadUniversalTrackers(blockingLoad = true) {
  return (dispatch: AppDispatch, getState: GetState) => {
    if (!getState().initiative.loaded) {
      return dispatch({
        type: actionTypes.ERROR_UNIVERSALTRACKERS,
        initiativeId: undefined
      });
    }

    const initiativeId = getState().initiative.data?._id;

    if (blockingLoad) {
      const currentState = getState().universalTrackers;
      if (currentState.initiativeId === initiativeId) {
        return;
      }

      if (!getState().initiative.data?.linkedUniversalTrackers) {
        return dispatch({
          type: actionTypes.NO_DATA_UNIVERSALTRACKERS,
          initiativeId: initiativeId
        });
      }

      dispatch({
        type: actionTypes.LOADING_UNIVERSALTRACKERS,
        initiativeId: initiativeId
      });
    }

    return apiClient.get('universal-trackers/initiativeId/' + initiativeId)
      .then(response => {
        dispatch({
          type: actionTypes.LOADED_UNIVERSALTRACKERS,
          data: response.data.data,
          initiativeId: initiativeId
        });

        dispatch(loadUniversalTrackerGroups());
      })
      .catch(handleError(dispatch, actionTypes.ERROR_UNIVERSALTRACKERS));
  }
}

export function loadUniversalTrackerValues(blockingLoad = true, surveyId?: string | null) {
  return (dispatch: AppDispatch, getState: GetState) => {
    if (!getState().initiative.loaded) {
      return dispatch({
        type: actionTypes.ERROR_UNIVERSALTRACKERVALUES,
        initiativeId: undefined,
        surveyId: undefined,
      });
    }
    const currentState = getState().universalTrackerValues;

    const initiativeId = getState().initiative.data?._id;
    // If it's a reload, need to remember previous surveyId

    if (blockingLoad) {
      if (currentState.initiativeId === initiativeId && currentState.surveyId === surveyId) {
        return;
      }

      if (!getState().initiative.data?.linkedUniversalTrackers) {
        return dispatch({
          type: actionTypes.NO_DATA_UNIVERSALTRACKERVALUES,
          initiativeId,
          surveyId,
        });
      }

      dispatch({
        type: actionTypes.LOADING_UNIVERSALTRACKERVALUES,
        initiativeId,
        surveyId
      });
    }

    return apiClient.get(`universal-tracker-values/initiativeId/${initiativeId}/recursive/${surveyId || ''}`)
      .then(response => {
        const currentState = getState().universalTrackerValues;
        if (currentState.surveyId !== surveyId) {
          // Stop race-condition if multiple requests are fired, then last one to request it wins
          return;
        }
        dispatch({
          type: actionTypes.LOADED_UNIVERSALTRACKERVALUES,
          data: response.data.data,
          initiativeId,
          surveyId,
        });
      })
      .catch(handleError(dispatch, actionTypes.ERROR_UNIVERSALTRACKERVALUES));
  }
}

export const reloadUniversalTrackers = () => loadUniversalTrackers(false);
export const reloadUniversalTrackerValues = () => {
  return (dispatch: AppDispatch, getState: GetState) => {
    const currentState = getState().universalTrackerValues;
    // Loaded for another initiative, so its surveyId would be sent with the current initiativeId
    if (currentState.initiativeId !== getState().initiative.data?._id) {
      return;
    }
    return dispatch(loadUniversalTrackerValues(false, currentState.surveyId));
  }
}

export function loadValueDetails(utrId: string, utrValueId: string, includeFiles = true) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const utrValueDetailsState = getState().universalTrackerValueDetails;
    if (utrValueDetailsState.universalTrackerValueId === utrValueId && utrValueDetailsState.universalTrackerId === utrId) {
      return;
    }

    dispatch({
      type: actionTypes.LOADING_UNIVERSALTRACKERVALUE_DETAILS,
      universalTrackerId: utrId,
      universalTrackerValueId: utrValueId
    });

    const options = { params: { includeFiles: includeFiles } };

    const apiRequest = apiClient.get(
      'universal-tracker-values/' + utrValueId,
      options
    );

    return apiRequest
      .then(response => {
        dispatch({
          type: actionTypes.LOADED_UNIVERSALTRACKERVALUE_DETAILS,
          data: response.data.data
        });
      })
      .catch(handleError(dispatch, actionTypes.ERROR_UNIVERSALTRACKERVALUE_DETAILS));
  }
}

export const loadValueInfoDetails = (ids: string[]) => (_dispatch: AppDispatch, _getState: GetState) => {
  const options = { params: { ids } };
  return apiClient.get<ApiResponse<ValueInfoDetailsResponse>>('universal-tracker-values/history/info', options)
    .then(response => response.data.data)
};

export function unloadValueDetails() {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: actionTypes.UNLOAD_UNIVERSALTRACKERVALUE_DETAILS
    });
  }
}

export function loadUniversalTrackerGroups() {
  return (dispatch: AppDispatch, getState: GetState) => {
    if (!getState().initiative.loaded) {
      return dispatch({
        type: actionTypes.ERROR_UNIVERSALTRACKERGROUPS,
        initiativeId: undefined
      });
    }

    const initiativeId = getState().initiative.data?._id;
    const state = getState().universalTrackerGroups;
    if (state.initiativeId === initiativeId) {
      return;
    }

    dispatch({
      type: actionTypes.LOADING_UNIVERSALTRACKERGROUPS,
      initiativeId: initiativeId
    });

    apiClient.get('universal-tracker-groups/initiativeId/' + initiativeId)
      .then(response => {

        dispatch({
          type: actionTypes.LOADED_UNIVERSALTRACKERGROUPS,
          data: response.data.data || [],
          initiativeId: initiativeId
        });
      })
      .catch(handleError(dispatch, actionTypes.ERROR_UNIVERSALTRACKERGROUPS));
  }
}

export function searchTracker(
  escapedString: string,
  filter: { valueType?: UtrValueType[] } = {},
  initiativeId?: string,
) {
  const params = queryString.stringify({
    s: escapedString,
    initiativeId: initiativeId,
    ...filter
  });
  return apiClient.get<ApiResponse<UTRSuggestion[]>>(`universal-trackers/search?${params}`)
    .then(response => response.data.data)
}

export const loadUtrvHistory = (id: string) => {
  return apiClient.get<ApiResponse<UtrvHistory>>(`universal-tracker-values/${id}/history`)
  .then(response => response.data.data)
};

export const loadUtrvHistoryAssurer = (portfolioId: string, utrvId: string) =>  {
  return apiClient.get<ApiResponse<UtrvHistory>>(`assurers/portfolios/${portfolioId}/utrv/${utrvId}/history`)
  .then(response => response.data.data)
};
